import React, { useCallback, useEffect, useReducer, useState } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { MobileSpacer } from 'components/mobile-spacer';
import Modal, { ModalContent } from 'components/modal';
import { DeliveryStatus } from 'generated/rbi-graphql';
import usePlaceIdDetails from 'hooks/geolocation/use-place-id-details';
import useErrorModal, { IErrorModal } from 'hooks/use-error-modal';
import usePlacesService from 'hooks/use-map/use-places-service';
import { StoreStatus, useGetClosestAvailableDeliveryRestaurant } from 'remote/api/restaurants';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, useMParticleContext } from 'state/mParticle';
import { useMenuContext } from 'state/menu';
import { useNetworkContext } from 'state/network';
import { ServiceMode, useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { IPlaceData, parsePlaceAddress } from 'utils/geolocation';
import { routes } from 'utils/routing';

import { CheckingAvailability } from './checking-availability';
import { DeliveryAddresses } from './delivery-addresses';
import { DeliveryUnavailable } from './delivery-unavailable';
import DeliveryAddressForm from './new-address-form/delivery-address-form';
import { ActionStatus, AddressModalAction, SelectedDeliveryAddress } from './types';

// iOS HACK to add spacing to the bottom of the form.
// Needed because the keyboard was covering the form inputs
const SpacerContainer = styled.div`
  display: block;
  width: 100%;
`;

const ModalContentWrapper = styled(ModalContent)<{ enableSimplifiedDelivery: boolean }>`
  & div[class*='modal-content__Inner'] {
    height: 100%;
    ${props =>
      props.enableSimplifiedDelivery &&
      css`
        // overwrite modal content set width for delivery address styling
        width: 100%;
        max-width: 100%;
      `}
  }
`;

export const formatPhoneNumber = (phoneNumber: string) => {
  // removes all non-digit characters
  return phoneNumber.replace(/[^\d]/g, '');
};

interface IDeliveryAddressModalProps {
  redirectUrl: string;
  dismissAddressModalUrl: string;
}

interface AddressModalState {
  error: IErrorModal | null;
  status: ActionStatus;
  address: string;
}

const addressReducer = (state: AddressModalState, action: AddressModalAction) => {
  switch (action.type) {
    case ActionStatus.IDLE:
    case ActionStatus.QUOTE_UNAVAILABLE: {
      return { ...state, error: null, status: action.type };
    }
    case ActionStatus.PENDING: {
      return { ...state, error: null, status: ActionStatus.PENDING, address: action.address };
    }
    case ActionStatus.ERROR: {
      return { ...state, error: action.error, status: ActionStatus.ERROR };
    }
    default: {
      throw new Error('Unrecognized state transition');
    }
  }
};

export const DeliveryAddressModal: React.FC<IDeliveryAddressModalProps> = ({
  redirectUrl,
  dismissAddressModalUrl,
}) => {
  const [state, dispatch] = useReducer(addressReducer, {
    error: null,
    address: '',
    status: ActionStatus.IDLE,
  });

  const { user } = useAuthContext();
  const { logEvent, logNavigationClick } = useMParticleContext();
  const order = useOrderContext();
  const { resetStore } = useStoreContext();
  const { formatMessage } = useIntl();
  const { connection, sanityEndpoints } = useNetworkContext();
  const { checkStaticMenuItemAvailability, selectedStaticMenuItemId } = useMenuContext();
  const deliveryRadius = useFlag(LaunchDarklyFlag.DELIVERY_RADIUS_IN_MILES);
  const getClosestAvailableDeliveryRestaurant = useGetClosestAvailableDeliveryRestaurant();
  const placesService = usePlacesService();
  const getDetails = usePlaceIdDetails();
  const navigate = useNavigate();

  const enableSimplifiedDelivery = useFlag(LaunchDarklyFlag.ENABLE_SIMPLE_DELIVERY_FORM);

  const enableUserSavedDeliveryAddressPhone = useFlag(
    LaunchDarklyFlag.ENABLE_USER_SAVED_DELIVERY_ADDRESS_PHONE
  );
  const shouldEnableUserSavedDeliveryAddressPhone = enableUserSavedDeliveryAddressPhone && !!user;

  const showDeliveryAddresses =
    shouldEnableUserSavedDeliveryAddressPhone && Boolean(user?.details?.deliveryAddresses?.length);

  const [createAddressView, setCreateAddressView] = useState(false);
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Delivery Not Completed',
  });

  const getPlaceId = useCallback(
    (addrString: string) => {
      return new Promise<string | undefined>((resolve, reject) => {
        const currentPlaces = placesService();
        if (!currentPlaces) {
          return;
        }

        currentPlaces.findPlaceFromQuery(
          { query: addrString, fields: ['place_id'] },
          (results, status) => {
            if (status !== window.google.maps.places.PlacesServiceStatus.OK || !results[0]) {
              return reject('Address not recognized');
            }
            return resolve(results[0].place_id!);
          }
        );
      });
    },
    [placesService]
  );

  const getDetailsPlaceData = useCallback(
    (pId: string | undefined): Promise<IPlaceData> => {
      return getDetails(pId).then(details => {
        if (details) {
          const placeData = {
            coordinates: {
              lat: details.geometry!.location.lat(),
              lng: details.geometry!.location.lng(),
            },
            address: parsePlaceAddress(details.address_components, { useISOCountry: true }),
          };

          return Promise.resolve(placeData);
        }

        logNavigationClick(CustomEventNames.BUTTON_CLICK_DELIVER_HERE, {
          error: 'Address Not Recognized',
        });

        return Promise.reject(details);
      });
    },
    [getDetails, logNavigationClick]
  );

  const {
    setDeliveryAddress,
    setDeliveryInstructions,
    setDeliveryPhoneNumber,
    selectStore,
    selectServiceMode,
  } = order;

  const completeDelivery = async (data: SelectedDeliveryAddress) => {
    if (!data.address) {
      return dispatch({
        type: ActionStatus.ERROR,
        error: { message: 'Missing address information for delivery' },
      });
    }

    const {
      address,
      shouldSaveAddress,
      deliveryInstructions,
      phoneNumber,
      coordinates: selectedCoordinates,
      placeId: selectedPlaceId,
    } = data;

    const addressString = `${address.addressLine1}, ${address.city}, ${address.state} ${address.zip}, ${address.country}`;
    dispatch({ type: ActionStatus.PENDING, address: addressString });

    setDeliveryAddress({
      ...address,
      shouldSave: shouldSaveAddress,
    });

    setDeliveryInstructions(deliveryInstructions);
    setDeliveryPhoneNumber(enableSimplifiedDelivery ? phoneNumber : formatPhoneNumber(phoneNumber));

    try {
      const placeId = selectedPlaceId || (await getPlaceId(addressString));
      const { coordinates } = selectedCoordinates
        ? { coordinates: selectedCoordinates }
        : await getDetailsPlaceData(placeId);

      const {
        deliveryQuote,
        storeStatus,
        store,
        nextEarliestOpen,
      } = await getClosestAvailableDeliveryRestaurant(
        sanityEndpoints.groq,
        deliveryRadius,
        {
          connection,
          userLat: coordinates.lat,
          userLng: coordinates.lng,
        },
        phoneNumber,
        logEvent,
        address
      );

      // if store available, route to store
      if (store && deliveryQuote === DeliveryStatus.QUOTE_SUCCESSFUL) {
        return selectStore(store, async () => {
          selectServiceMode(ServiceMode.DELIVERY);
          // If user selected item from static menu, make sure it is available at store
          if (selectedStaticMenuItemId) {
            await checkStaticMenuItemAvailability(store);
            return;
          }

          navigate(redirectUrl);
        });
      }

      if (deliveryQuote === DeliveryStatus.QUOTE_UNAVAILABLE) {
        return dispatch({ type: ActionStatus.QUOTE_UNAVAILABLE });
      }

      if (deliveryQuote === DeliveryStatus.QUOTE_ERROR) {
        return dispatch({
          type: ActionStatus.ERROR,
          error: {
            message: formatMessage({ id: 'pleaseTryAgainLater' }),
            modalAppearanceEventMessage: 'Error: Delivery Quote Error',
          },
        });
      }

      // clear service mode and the store if delivery is not available
      if (storeStatus === StoreStatus.NO_DELIVERY) {
        selectServiceMode(null);
        resetStore();
      }

      // otherwise present current store status modal
      navigate(routes[storeStatus], { state: { nextEarliestOpen } });
    } catch (error) {
      dispatch({
        type: ActionStatus.ERROR,
        error: { message: 'Error complete Delivery', error },
      });
    }
  };

  const handleDismiss = () => {
    navigate(dismissAddressModalUrl);
  };

  useEffect(() => {
    if (state.status === ActionStatus.ERROR && state.error) {
      openErrorDialog(state.error);
    }
  }, [openErrorDialog, state.error, state.status]);

  return (
    <>
      <Modal
        onDismiss={handleDismiss}
        mParticleEventData={{
          modalAppearanceEventMessage: 'Select your delivery address',
        }}
        skipLoggingHeaderAndMessage={true}
      >
        <ModalContentWrapper enableSimplifiedDelivery={enableSimplifiedDelivery}>
          {state.status === ActionStatus.PENDING ? (
            <CheckingAvailability />
          ) : state.status === ActionStatus.QUOTE_UNAVAILABLE ? (
            <DeliveryUnavailable
              address={state.address}
              close={() => dispatch({ type: ActionStatus.IDLE })}
            />
          ) : (
            <SpacerContainer>
              {showDeliveryAddresses && !createAddressView ? (
                <DeliveryAddresses
                  addresses={user?.details?.deliveryAddresses || []}
                  onCreate={() => setCreateAddressView(true)}
                  setAddress={completeDelivery}
                />
              ) : (
                <DeliveryAddressForm
                  instructions={order.deliveryInstructions}
                  getDetailsPlaceData={getDetailsPlaceData}
                  setCreateAddressView={setCreateAddressView}
                  showDeliveryAddresses={showDeliveryAddresses}
                  user={user}
                  setAddress={completeDelivery}
                />
              )}
              <MobileSpacer />
            </SpacerContainer>
          )}
        </ModalContentWrapper>
        <ErrorDialog data-testid="address-modal-error-dialog" />
      </Modal>
    </>
  );
};
